import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { extractErrorMessage } from '../utils/apitools'
import { blobErrorMessage } from '../utils/blobErrorMessage'
import { guidedBase } from './useGuidedResponse'
import { adminGuidesKey } from './useAdminGuides'
import type { Guide, GuideBody, GuideInviteResult, GuideNote, GuideNoteCuration, GuideResponse, GuideResponseDetail } from '../types/gotypes'

/**
 * One guide, for a Manager (28 Oct 2026): its agenda (saved with the version
 * it was read at; a stale save is refused with 409), Close, invitations,
 * Withdraw, every response with its states and notes, a note's tag, and the
 * export. Routes: goapi/pkg/server/guided.go.
 */

export const adminGuideKey = (customer: string, guideId: string) => ['guided', customer, 'guide', guideId]
export const adminGuideResponsesKey = (customer: string, guideId: string) => ['guided', customer, 'guide', guideId, 'responses']

/** A save: saved, or refused because someone saved the guide since it was read. */
export type GuideSaveResult = { status: 'saved'; guide: Guide } | { status: 'stale' } | { status: 'error'; error: string }

export type GuideExportFormat = 'xlsx' | 'md'

const EXPORT_TYPES: Record<GuideExportFormat, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  md: 'text/markdown',
}

/** "Month-end close" → "month-end-close.xlsx". */
export function exportFileName(title: string, format: GuideExportFormat): string {
  const stem = title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'guide'
  return `${stem}-responses.${format}`
}

export interface UseAdminGuideOptions {
  customer: string
  guideId: string
  /** Load the responses (the People and Responses tabs). */
  withResponses?: boolean
}

export default function useAdminGuide({ customer, guideId, withResponses = false }: UseAdminGuideOptions) {
  const queryClient = useQueryClient()
  const base = guidedBase(customer)
  const guidePath = `${base}/guides/${encodeURIComponent(guideId)}`
  const guideKey = adminGuideKey(customer, guideId)
  const responsesKey = adminGuideResponsesKey(customer, guideId)

  const guideQuery = useQuery<Guide, Error>({
    queryKey: guideKey,
    enabled: Boolean(customer && guideId),
    queryFn: async () => {
      try {
        return (await axios.get<Guide>(guidePath)).data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })
  const responsesQuery = useQuery<GuideResponseDetail[], Error>({
    queryKey: responsesKey,
    enabled: Boolean(customer && guideId && withResponses),
    queryFn: async () => {
      try {
        return (await axios.get<{ responses: GuideResponseDetail[] }>(`${guidePath}/responses`)).data.responses ?? []
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  const [busy, setBusy] = useState<'' | 'save' | 'close' | 'invite' | 'withdraw' | 'export' | 'curation'>('')
  const [actionError, setActionError] = useState('')

  const setGuide = (guide: Guide) => {
    queryClient.setQueryData(guideKey, guide)
    void queryClient.invalidateQueries({ queryKey: adminGuidesKey(customer), exact: true })
  }
  const refreshResponses = () => {
    void queryClient.invalidateQueries({ queryKey: responsesKey })
    // The counts on the guide and the list move with the responses.
    void queryClient.invalidateQueries({ queryKey: guideKey, exact: true })
    void queryClient.invalidateQueries({ queryKey: adminGuidesKey(customer), exact: true })
  }

  /** Save the agenda. `body.current_version` is the version it was read at. */
  const handleSave = async (body: GuideBody): Promise<GuideSaveResult> => {
    setBusy('save')
    try {
      const { data } = await axios.put<Guide>(guidePath, body)
      setGuide(data)
      return { status: 'saved', guide: data }
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 409) return { status: 'stale' }
      return { status: 'error', error: extractErrorMessage(error) }
    } finally {
      setBusy('')
    }
  }

  const handleClose = async (): Promise<boolean> => {
    setBusy('close')
    setActionError('')
    try {
      const { data } = await axios.post<Guide>(`${guidePath}/close`)
      setGuide(data)
      return true
    } catch (error) {
      setActionError(extractErrorMessage(error))
      return false
    } finally {
      setBusy('')
    }
  }

  /** Invite these emails. Returns one result per email, or null with actionError set. */
  const handleInvite = async (emails: string[]): Promise<GuideInviteResult[] | null> => {
    setBusy('invite')
    setActionError('')
    try {
      const { data } = await axios.post<{ results: GuideInviteResult[] }>(`${guidePath}/invites`, { emails })
      refreshResponses()
      return data.results ?? []
    } catch (error) {
      setActionError(extractErrorMessage(error))
      return null
    } finally {
      setBusy('')
    }
  }

  const handleWithdraw = async (responseId: string): Promise<GuideResponse | null> => {
    setBusy('withdraw')
    setActionError('')
    try {
      const { data } = await axios.post<GuideResponse>(`${base}/responses/${encodeURIComponent(responseId)}/withdraw`)
      refreshResponses()
      return data
    } catch (error) {
      setActionError(extractErrorMessage(error))
      return null
    } finally {
      setBusy('')
    }
  }

  /** Tag a note for the workshop. The responses list takes the saved note. */
  const handleCuration = async (noteId: string, curation: GuideNoteCuration, curationNote: string): Promise<boolean> => {
    setBusy('curation')
    setActionError('')
    try {
      const { data } = await axios.patch<GuideNote>(`${base}/notes/${encodeURIComponent(noteId)}/curation`, { curation, curation_note: curationNote })
      queryClient.setQueryData<GuideResponseDetail[]>(responsesKey, (prev) =>
        prev?.map((r) => ({ ...r, notes: r.notes.map((n) => (n.id === data.id ? data : n)) })),
      )
      return true
    } catch (error) {
      setActionError(extractErrorMessage(error))
      return false
    } finally {
      setBusy('')
    }
  }

  /** Download every response as a workbook (stamped by the server) or as Markdown. */
  const handleExport = async (format: GuideExportFormat): Promise<boolean> => {
    setBusy('export')
    setActionError('')
    try {
      const response = await axios.get(`${guidePath}/export`, { params: { format }, responseType: 'blob' })
      const blob = new Blob([response.data], { type: EXPORT_TYPES[format] })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = exportFileName(guideQuery.data?.title ?? '', format)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      return true
    } catch (error) {
      setActionError(await blobErrorMessage(error))
      return false
    } finally {
      setBusy('')
    }
  }

  return {
    guide: guideQuery.data ?? null,
    isLoading: guideQuery.isLoading,
    error: guideQuery.error?.message ?? '',
    reload: () => { void guideQuery.refetch() },
    responses: responsesQuery.data ?? [],
    responsesLoading: responsesQuery.isLoading && withResponses,
    responsesError: responsesQuery.error?.message ?? '',
    busy,
    actionError,
    clearActionError: () => setActionError(''),
    handleSave,
    handleClose,
    handleInvite,
    handleWithdraw,
    handleCuration,
    handleExport,
  }
}
